import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  MapPin,
  Copy,
  ExternalLink,
  ArrowLeft,
  Edit,
  Trash2,
  Plus,
  Calendar,
  User,
  Building,
  FileText,
  Eye,
  LandPlot,
  Navigation,
} from "lucide-react";
import api from "../../../../utils/api";
import {
  useToast,
  Button,
  Card,
  EmptyState,
} from "../components/AdminSharedComponents";
import ReportModal from "../report/ReportModal";

// Fix for default markers in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const ShowProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // NEW STATES for reports
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Tambahkan state untuk menghindari refetch tidak perlu
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (!id || hasLoaded) return;

    let isMounted = true;
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        const projectResponse = await api.get(`/api/projects/${id}`, {
          signal: controller.signal,
        });

        const reportsResponse = await api.get(`/api/reports/project/${id}`, {
          signal: controller.signal,
        });

        if (isMounted) {
          setProject(projectResponse.data);
          setReports(reportsResponse.data);
          setHasLoaded(true);
        }
      } catch (error) {
        if (error.name !== "CanceledError") {
          console.error("Error fetching project or reports:", error);
          toast.error("Failed to load project or related reports");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, hasLoaded]);

  // Initialize map when project data is loaded
  useEffect(() => {
    if (project && mapRef.current && !mapInstanceRef.current) {
      const hasCoordinates = project.coordinates?.coordinates;

      const defaultLat = hasCoordinates
        ? project.coordinates.coordinates[1]
        : -7.2504;
      const defaultLng = hasCoordinates
        ? project.coordinates.coordinates[0]
        : 112.7688;
      const defaultZoom = hasCoordinates ? 15 : 10;

      mapInstanceRef.current = L.map(mapRef.current).setView(
        [defaultLat, defaultLng],
        defaultZoom
      );

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(mapInstanceRef.current);

      if (hasCoordinates) {
        const marker = L.marker([defaultLat, defaultLng]).addTo(
          mapInstanceRef.current
        );
        marker.bindPopup(
          `<b>${project.name}</b><br/>${project.location || "Project Location"}`
        );
      }
    }
  }, [project]);

  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const handleDelete = async (deleteReports = false) => {
    try {
      await api.delete(`/api/projects/${id}?deleteReports=${deleteReports}`);
      toast.success(
        deleteReports
          ? "Project and all reports deleted successfully"
          : "Project deleted successfully"
      );
      navigate("/admin/projects");
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    }
    setShowDeleteModal(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const baseStyle =
      "px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium";
    switch (status) {
      case "Completed":
        return `${baseStyle} bg-green-100 text-green-800`;
      case "In Progress":
        return `${baseStyle} bg-blue-100 text-blue-800`;
      case "On Hold":
        return `${baseStyle} bg-yellow-100 text-yellow-800`;
      case "Cancelled":
        return `${baseStyle} bg-red-100 text-red-800`;
      default:
        return `${baseStyle} bg-gray-100 text-gray-800`;
    }
  };

  const handleCopyCoordinates = () => {
    if (project.coordinates?.coordinates) {
      const lat = project.coordinates.coordinates[1];
      const lng = project.coordinates.coordinates[0];
      navigator.clipboard.writeText(`${lat}, ${lng}`);
      toast.success("Coordinates copied to clipboard!");
    }
  };

  const handleOpenInGoogleMaps = () => {
    if (project.coordinates?.coordinates) {
      const lat = project.coordinates.coordinates[1];
      const lng = project.coordinates.coordinates[0];
      window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex justify-center items-center z-50 bg-gray-50">
        <div className="animate-pulse text-lg font-semibold text-primary">
          Loading project...
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center px-4">
          <h2 className="text-xl font-semibold mb-4 text-primary-dark">
            Project not found
          </h2>
          <Link to="/admin/projects">
            <Button icon={ArrowLeft}>Back to Projects</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-0">
      {/* Desktop Header - Sticky */}
      <div className="hidden md:block sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link
                to="/admin/projects"
                className="flex items-center gap-2 text-base text-primary hover:text-primary-dark transition-colors mr-6"
              >
                <ArrowLeft className="h-5 w-5" />
                Back to Projects
              </Link>
              <div className="flex items-center gap-4">
                <h1 className="text-2xl font-bold text-primary-dark">
                  {project.name}
                </h1>
                <span className={getStatusBadge(project.status)}>
                  {project.status}
                </span>
              </div>
            </div>

            {/* Desktop Action Buttons */}
            <div className="flex items-center gap-3">
              <Link to={`/admin/projects/edit/${project.id}`}>
                <Button icon={Edit}>Edit Project</Button>
              </Link>
              <Button
                variant="danger"
                icon={Trash2}
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Header - Sticky */}
      <div className="md:hidden sticky top-[72px] z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex justify-between items-center p-4">
          {/* Back Button */}
          <Link
            to="/admin/projects"
            className="flex items-center gap-2 text-base text-primary hover:text-primary-dark transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="leading-none">Back</span>
          </Link>

          {/* Title + Badge */}
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-primary-dark">
              {project.name}
            </h1>
            <span className={getStatusBadge(project.status)}>
              {project.status}
            </span>
          </div>
        </div>
      </div>

      {/* Mobile Floating Action Buttons - Improved */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 shadow-lg">
        <div className="px-4 py-3">
          <div className="grid gap-2 grid-cols-2">
            <Button
              variant="danger"
              icon={Trash2}
              onClick={() => setShowDeleteModal(true)}
              className="w-full py-2"
            >
              Delete
            </Button>
            <Link to={`/admin/projects/edit/${project.id}`}>
              <Button icon={Edit} className="w-full py-2">
                Edit Project
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 sm:px-6 py-4 sm:py-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-8">
            {/* Left Column - Project Details */}
            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-primary-dark">
                  Project Details
                </h2>
              </div>

              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium mb-1 text-gray-600">
                      Project Name
                    </label>
                    <p className="text-base font-medium text-primary-dark break-words">
                      {project.name}
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium mb-1 text-gray-600">
                      <Building className="h-4 w-4" />
                      Client Name
                    </label>
                    <p className="text-sm sm:text-base text-primary-dark break-words">
                      {project.client_name || "Not specified"}
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium mb-1 text-gray-600">
                      <User className="h-4 w-4" />
                      Person In Charge
                    </label>
                    <p className="text-sm sm:text-base text-primary-dark break-words">
                      {project.pic || "Not assigned"}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-600">
                      Status
                    </label>
                    <span className={getStatusBadge(project.status)}>
                      {project.status}
                    </span>
                  </div>

                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium mb-1 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      Start Date
                    </label>
                    <p className="text-sm sm:text-base text-primary-dark">
                      {formatDate(project.start_date)}
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium mb-1 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      End Date
                    </label>
                    <p className="text-sm sm:text-base text-primary-dark">
                      {formatDate(project.end_date)}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-1 text-sm font-medium mb-1 text-gray-600">
                    <MapPin className="h-4 w-4" />
                    Location
                  </label>
                  <p className="text-sm sm:text-base text-primary-dark break-words">
                    {project.location || "Location not specified"}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 pt-4 border-t border-gray-200">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-600">
                      Created
                    </label>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {formatDate(project.created_at)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-600">
                      Last Updated
                    </label>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {formatDate(project.updated_at)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Related Reports */}
            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                <div className="flex flex-col md:flex-row sm:items-center md:justify-between gap-3 w-full">
                  <h2 className="text-lg font-semibold text-primary-dark">
                    {reports.length > 0
                      ? `Related Reports (${reports.length})`
                      : "No Related Reports"}
                  </h2>

                  <Link
                    to={`/admin/reports/add/${id}`}
                    className="sm:w-auto w-full"
                  >
                    <Button icon={Plus} className="w-full sm:w-auto">
                      Add Report
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                {reports.length > 0 ? (
                  <div className="grid grid-cols-1 gap-3 sm:gap-4 max-h-96 overflow-y-auto">
                    {reports.map((report) => (
                      <Card
                        key={report.id}
                        className="!p-3 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => {
                          setSelectedReport(report);
                          setIsModalOpen(true);
                        }}
                      >
                        <p className="text-sm sm:text-base font-medium text-primary-dark break-words">
                          {report.title}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mt-1">
                          {report.description}
                        </p>
                        <div className="flex items-center justify-between mt-2 gap-2">
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <Calendar className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">
                              {new Date(report.created_at).toLocaleDateString()}
                            </span>
                          </span>
                          <div className="text-xs sm:text-sm inline-flex items-center text-primary-dark flex-shrink-0">
                            <Eye className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">
                              View Details
                            </span>
                            <span className="sm:hidden">View</span>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={FileText}
                    message="No reports associated with this project yet."
                    buttonLabel="Create First Report"
                    buttonIcon={Plus}
                    buttonLink={`/admin/reports/add?project_id=${id}`}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Location & Map Section - Now moved below */}
          <div className="mt-4 sm:mt-8 bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-dark">
                Project Location
              </h2>

              <div className="flex items-center gap-2">
                <Link to={`/admin/projects/map?projectId=${project.id}`}>
                  <Button
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    <Navigation className="h-4 w-4" />
                    <span>Show in Map</span>
                  </Button>
                </Link>
                <Link
                  to={`/admin/projects/excavation/map?projectId=${project.id}`}
                >
                  <Button variant="primary" className="flex items-center gap-1">
                    <LandPlot className="h-5 w-5" />
                    Excavation Map
                  </Button>
                </Link>
              </div>
            </div>

            <div className="p-4 md:p-6">
              {project.coordinates?.coordinates ? (
                <div className="space-y-4 md:space-y-6">
                  {/* Mobile Coordinates Info - Only show on mobile */}
                  <div className="md:hidden bg-primary/5 rounded-lg border border-primary/20 overflow-hidden">
                    <div className="p-3 sm:p-5">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                        <h4 className="text-sm sm:text-base font-semibold text-primary-dark">
                          GPS Coordinates
                        </h4>
                        <div className="flex flex-row items-stretch gap-2">
                          <Button
                            variant="secondary"
                            className="flex-1 sm:flex-none flex items-center justify-center gap-1 text-xs px-2 py-1.5 h-auto"
                            onClick={handleCopyCoordinates}
                          >
                            <Copy className="w-3 h-3" />
                            <span className="hidden sm:inline">
                              Copy Coordinates
                            </span>
                            <span className="sm:hidden">Copy</span>
                          </Button>

                          <Button
                            variant="secondary"
                            className="flex-1 sm:flex-none flex items-center justify-center gap-1 text-xs px-2 py-1.5 h-auto"
                            onClick={handleOpenInGoogleMaps}
                          >
                            <ExternalLink className="w-3 h-3" />
                            <span className="hidden sm:inline">
                              Open G-Maps
                            </span>
                            <span className="sm:hidden">Open G-Maps</span>
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                        <div>
                          <span className="text-gray-600 text-xs sm:text-sm">
                            Latitude
                          </span>
                          <div className="bg-white/50 rounded-md px-2 py-1 mt-1 border border-gray-200">
                            <code className="font-mono text-xs sm:text-sm text-primary-dark break-all">
                              {project.coordinates.coordinates[1].toFixed(6)}
                            </code>
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600 text-xs sm:text-sm">
                            Longitude
                          </span>
                          <div className="bg-white/50 rounded-md px-2 py-1 mt-1 border border-gray-200">
                            <code className="font-mono text-xs sm:text-sm text-primary-dark break-all">
                              {project.coordinates.coordinates[0].toFixed(6)}
                            </code>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Map - Single container for both desktop and mobile */}
                  <div className="relative border border-gray-300 rounded-lg overflow-hidden shadow-inner">
                    <div ref={mapRef} className="h-[450px] rounded-lg z-[1]" />

                    {/* Desktop Coordinates overlay */}
                    <div className="hidden md:block absolute top-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-3 min-w-64">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-700">
                          GPS Coordinates
                        </h4>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-600">Lat:</span>
                          <code className="font-mono text-gray-800 bg-gray-50 px-2 py-1 rounded">
                            {project.coordinates.coordinates[1].toFixed(6)}
                          </code>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-600">Lng:</span>
                          <code className="font-mono text-gray-800 bg-gray-50 px-2 py-1 rounded">
                            {project.coordinates.coordinates[0].toFixed(6)}
                          </code>
                        </div>
                        <div className="flex gap-1 pt-1">
                          <Button
                            variant="primary"
                            className="flex-1 text-xs px-2 py-1 h-auto"
                            onClick={handleCopyCoordinates}
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Copy
                          </Button>
                          <Button
                            variant="secondary"
                            className="flex-1 text-xs px-2 py-1 h-auto"
                            onClick={handleOpenInGoogleMaps}
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            Maps
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <div className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mb-4">
                    <MapPin className="h-full w-full" />
                  </div>
                  <h3 className="text-base sm:text-lg font-medium mb-2 text-primary-dark">
                    No Location Set
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 max-w-sm mx-auto px-4">
                    This project doesn't have coordinates assigned yet. Add
                    location to see it on the map.
                  </p>
                  <Link to={`/admin/projects/edit/${id}`}>
                    <Button icon={Plus} className="text-sm">
                      Add Location
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Report Modal */}
      {isModalOpen && selectedReport && (
        <ReportModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          report={selectedReport}
          project={project}
        />
      )}

      {/* Delete Modal - Mobile Optimized */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-primary-dark">
                Delete Project
              </h3>
            </div>
            <p className="text-sm sm:text-base text-gray-600 mb-6">
              Are you sure you want to delete "
              <strong className="break-words">{project.name}</strong>"? This
              action cannot be undone.
            </p>

            <div className="space-y-2 sm:space-y-3">
              <Button
                variant="danger"
                className="w-full text-sm sm:text-base py-2"
                onClick={() => handleDelete(false)}
              >
                Delete Project Only
              </Button>

              <Button
                variant="secondary"
                className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 text-sm sm:text-base py-2"
                onClick={() => handleDelete(true)}
              >
                Delete Project + Reports
              </Button>

              <Button
                variant="secondary"
                className="w-full text-sm sm:text-base py-2"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShowProject;
